import { useState, useEffect, Fragment, useContext } from 'react';
import StaffNav from './managernav';
import { AuthContext } from '../AuthContext';


const formatDateTime = (value) => {
  if (!value) return 'N/A';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? 'N/A' : date.toLocaleString();
};

export default function Assessment({ embedded = false }) {
  const [activeTab, setActiveTab] = useState('create');
  const [assessmentScope, setAssessmentScope] = useState('global');
  const [prompt, setPrompt] = useState('');
  
  // Global assessment state
  const [globalType, setGlobalType] = useState('');
  const [globalCategory, setGlobalCategory] = useState('');
  const [globalDifficulty, setGlobalDifficulty] = useState('');
  
  // Role-specific assessment state
  const [roles, setRoles] = useState([]);
  const [selectedRole, setSelectedRole] = useState('');
  const [roleCategory, setRoleCategory] = useState('');
  const [roleDifficulty, setRoleDifficulty] = useState('');
  
  // Current assessments state
  const [assessments, setAssessments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState(null);

  // Generated assessment state
  const [generatedAssessment, setGeneratedAssessment] = useState(null);
  const [generatedMeta, setGeneratedMeta] = useState(null);
  const [creating, setCreating] = useState(false);
  const [savingAssessment, setSavingAssessment] = useState(false);
  const [saveFeedback, setSaveFeedback] = useState(null);
  const [deletingCode, setDeletingCode] = useState(null);
  const [expandedRows, setExpandedRows] = useState([]);


  const user = useContext(AuthContext);

  // Fetch roles when component mounts
  useEffect(() => {
    const fetchRoles = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/allroles', {
          credentials: 'include',
        });
        const data = await response.json();
        if (data.success) {
          setRoles(data.data || []);
        }
      } catch (error) {
        console.error('Failed to fetch roles:', error);
      }
    };
    fetchRoles();
  }, []);

  // Fetch current assessments when tab changes
  useEffect(() => {
    if (activeTab === 'current') {
      fetchAssessments();
    }
  }, [activeTab]);

  const fetchAssessments = async () => {
    setLoading(true);
    setListError(null);
    try {
      const response = await fetch('https://analytics.insightos.co.uk/assesment/list', {
        credentials: 'include',
      });
      const data = await response.json();
      if (data.success) {
        setAssessments(data.data || []);
      } else {
        setAssessments([]);
        setListError(data.message || 'Failed to load assessments');
      }
    } catch (error) {
      console.error('Failed to fetch assessments:', error);
      setAssessments([]);
      setListError('An error occurred while loading assessments');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setCreating(true);
    setSaveFeedback(null);

    const payload = {
      prompt,
      assessmentScope,
    };

    if (assessmentScope === 'global') {
      payload.globalType = globalType;
      payload.globalCategory = globalCategory;
      payload.globalDifficulty = globalDifficulty;
    } else {
      payload.selectedRole = selectedRole;
      payload.roleCategory = roleCategory;
      payload.roleDifficulty = roleDifficulty;
    }

    try {
      const response = await fetch('https://api.insightos.co.uk/api/aiassessment/new', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
            Authorization: `Bearer ${user.analyticsToken}`
         },
         credentials: 'include',
        body: JSON.stringify(payload),
      });

      const data = await response.json();
      if (data.success && data.assessment) {
        const { companyCode, assessmentCode, ...displayData } = data.assessment;
        setGeneratedAssessment({
          ...displayData,
          questions: Array.isArray(displayData.questions) ? displayData.questions : [],
        });
        setGeneratedMeta({ companyCode, assessmentCode });
        // Reset form for next creation
        setPrompt('');
        setGlobalType('');
        setGlobalCategory('');
        setGlobalDifficulty('');
        setSelectedRole('');
        setRoleCategory('');
        setRoleDifficulty('');
      } else {
        const reason = data.message || 'Failed to create assessment';
        setSaveFeedback({ type: 'error', message: reason });
      }
    } catch (error) {
      console.error('Failed to create assessment:', error);
      setSaveFeedback({ type: 'error', message: 'An error occurred while creating the assessment' });
    } finally {
      setCreating(false);
    }
  };

  const handleAssessmentMetaChange = (field, value) => {
    setGeneratedAssessment((prev) => (prev ? { ...prev, [field]: value } : prev));
  };

  const handleQuestionChange = (index, field, value) => {
    setGeneratedAssessment((prev) => {
      if (!prev) return prev;
      const updatedQuestions = prev.questions.map((question, idx) =>
        idx === index ? { ...question, [field]: value } : question
      );
      return { ...prev, questions: updatedQuestions };
    });
  };

  const handleSaveAssessment = async () => {
    if (!generatedAssessment) return;
    setSavingAssessment(true);
    setSaveFeedback(null);

    const payload = {
      ...generatedAssessment,
      ...generatedMeta,
    };

    try {
      const response = await fetch('https://analytics.insightos.co.uk/assesment/save', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json',
            Authorization: `Bearer ${user.analyticsToken}`
         },
        body: JSON.stringify(payload),
      });
      const data = await response.json();
      if (data.success) {
        setSaveFeedback({ type: 'success', message: 'Assessment saved successfully.' });
        setGeneratedAssessment(null);
        setGeneratedMeta(null);
        setActiveTab('current');
        fetchAssessments();
      } else {
        setSaveFeedback({ type: 'error', message: data.message || 'Failed to save assessment.' });
      }
    } catch (error) {
      console.error('Failed to save assessment:', error);
      setSaveFeedback({ type: 'error', message: 'An error occurred while saving the assessment.' });
    } finally {
      setSavingAssessment(false);
    }
  };

  const handleDeleteAssessment = async (assessment) => {
    if (!assessment?.assessmentCode) return;
    setDeletingCode(assessment.assessmentCode);
    setListError(null);

    try {
      const response = await fetch('https://analytics.insightos.co.uk/assesment/delete', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' ,
            Authorization: `Bearer ${user.analyticsToken}`
         },
        body: JSON.stringify({
          assessmentCode: assessment.assessmentCode,
          companyCode: assessment.companyCode,
        }),
      });
      const data = await response.json();
      if (!data.success) {
        setListError(data.message || 'Failed to delete assessment');
      } else {
        fetchAssessments();
      }
    } catch (error) {
      console.error('Failed to delete assessment:', error);
      setListError('An error occurred while deleting the assessment');
    } finally {
      setDeletingCode(null);
    }
  };

  const handleToggleQuestions = (rowKey) => {
    setExpandedRows((prev) =>
      prev.includes(rowKey) ? prev.filter((key) => key !== rowKey) : [...prev, rowKey]
    );
  };

  const outerClass = embedded
    ? 'py-2'
    : 'min-h-screen bg-gradient-to-br from-indigo-50 to-white p-6';

  return (
    <>
      {!embedded && <StaffNav />}
      <div className={outerClass}>
        <div className="max-w-6xl mx-auto">
          <h1 className="text-3xl font-bold text-gray-900 mb-8">Assessment Management</h1>

          {/* Tab Navigation */}
          <div className="flex gap-4 mb-6 border-b border-gray-200">
            <button
              onClick={() => setActiveTab('create')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'create'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Create New Assessment
            </button>
            <button
              onClick={() => setActiveTab('current')}
              className={`px-6 py-3 font-semibold transition-colors ${
                activeTab === 'current'
                  ? 'text-indigo-600 border-b-2 border-indigo-600'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              Current Assessments
            </button>
          </div>

          {/* Tab Content */}
          {activeTab === 'create' ? (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Create New Assessment</h2>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Prompt Box */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    AI Instructions
                  </label>
                  <textarea
                    rows={4}
                    className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                    placeholder="Example: Generate a beginner React.js assessment with 10 multiple-choice questions"
                    value={prompt}
                    onChange={(e) => setPrompt(e.target.value)}
                    required
                  />
                </div>

                {/* Assessment Scope Toggle */}
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-3">
                    Assessment Scope
                  </label>
                  <div className="flex gap-4">
                    <button
                      type="button"
                      onClick={() => setAssessmentScope('global')}
                      className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
                        assessmentScope === 'global'
                          ? 'bg-indigo-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Global
                    </button>
                    <button
                      type="button"
                      onClick={() => setAssessmentScope('role-specific')}
                      className={`flex-1 py-3 px-6 rounded-lg font-semibold transition-all ${
                        assessmentScope === 'role-specific'
                          ? 'bg-indigo-600 text-white shadow-lg'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      Role-Specific
                    </button>
                  </div>
                </div>

                {/* Global Assessment Options */}
                {assessmentScope === 'global' && (
                  <div className="space-y-4 p-6 bg-indigo-50 rounded-lg">
                    <h3 className="text-lg font-semibold text-indigo-900 mb-4">
                      Global Assessment Options
                    </h3>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Type
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        value={globalType}
                        onChange={(e) => setGlobalType(e.target.value)}
                        required
                      >
                        <option value="">Select Type</option>
                        <option value="hybrid-personal-logic">Hybrid-Personal-Logic</option>
                        <option value="logic-based">Logic-Based</option>
                        <option value="culture-based">Culture-Based</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Category
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        value={globalCategory}
                        onChange={(e) => setGlobalCategory(e.target.value)}
                        required
                      >
                        <option value="">Select Category</option>
                        <option value="technical">Technical</option>
                        <option value="general">General</option>
                        <option value="culture">Culture</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Difficulty
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        value={globalDifficulty}
                        onChange={(e) => setGlobalDifficulty(e.target.value)}
                        required
                      >
                        <option value="">Select Difficulty</option>
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Role-Specific Assessment Options */}
                {assessmentScope === 'role-specific' && (
                  <div className="space-y-4 p-6 bg-purple-50 rounded-lg">
                    <h3 className="text-lg font-semibold text-purple-900 mb-4">
                      Role-Specific Assessment Options
                    </h3>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Select Role
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                        value={selectedRole}
                        onChange={(e) => setSelectedRole(e.target.value)}
                        required
                      >
                        <option value="">Select a Role</option>
                        {roles.map((role) => (
                          <option key={role.id} value={role.Name}>
                            {role.Name} {role.Description ? `- ${role.Description}` : ''}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Assessment Category
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                        value={roleCategory}
                        onChange={(e) => setRoleCategory(e.target.value)}
                        required
                      >
                        <option value="">Select Category</option>
                        <option value="technical">Technical</option>
                        <option value="general">General</option>
                        <option value="culture">Culture</option>
                        <option value="hybrid">Hybrid</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">
                        Difficulty
                      </label>
                      <select
                        className="w-full border border-gray-300 rounded-lg px-4 py-3 focus:ring-2 focus:ring-purple-500 focus:border-purple-500 outline-none"
                        value={roleDifficulty}
                        onChange={(e) => setRoleDifficulty(e.target.value)}
                        required
                      >
                        <option value="">Select Difficulty</option>
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>
                )}

                {/* Submit Button */}
                <div className="flex justify-end pt-4">
                  <button
                    type="submit"
                    disabled={creating}
                    className="px-8 py-3 bg-green-600 text-white font-semibold rounded-lg shadow-lg hover:bg-green-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                  >
                    {creating ? 'Creating Assessment...' : 'Create Assessment'}
                  </button>
                </div>
              </form>

              {saveFeedback && (
                <div
                  className={`mt-6 rounded-lg px-4 py-3 text-sm font-semibold ${
                    saveFeedback.type === 'success'
                      ? 'bg-green-50 text-green-700'
                      : 'bg-red-50 text-red-700'
                  }`}
                >
                  {saveFeedback.message}
                </div>
              )}

              {generatedAssessment && (
                <div className="mt-10 border border-gray-200 rounded-2xl p-6 bg-gray-50">
                  <div className="flex flex-wrap items-center gap-4 mb-6">
                    <h3 className="text-xl font-bold text-gray-900">Generated Assessment</h3>
                    <span className="px-3 py-1 text-xs font-bold rounded-full bg-indigo-100 text-indigo-700">
                      Scope: {generatedAssessment.scope}
                    </span>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
                    {generatedAssessment.scope === 'global' && (
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Type</label>
                        <input
                          type="text"
                          value={generatedAssessment.type || ''}
                          onChange={(e) => handleAssessmentMetaChange('type', e.target.value)}
                          className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                        />
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Role</label>
                      <input
                        type="text"
                        value={generatedAssessment.role || ''}
                        onChange={(e) => handleAssessmentMetaChange('role', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Category</label>
                      <input
                        type="text"
                        value={generatedAssessment.category || ''}
                        onChange={(e) => handleAssessmentMetaChange('category', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Difficulty</label>
                      <select
                        value={generatedAssessment.difficulty || ''}
                        onChange={(e) => handleAssessmentMetaChange('difficulty', e.target.value)}
                        className="w-full border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      >
                        <option value="">Select Difficulty</option>
                        <option value="beginner">Beginner</option>
                        <option value="intermediate">Intermediate</option>
                        <option value="advanced">Advanced</option>
                      </select>
                    </div>
                  </div>

                  <div className="space-y-6 max-h-[32rem] overflow-y-auto pr-2">
                    {(generatedAssessment.questions || []).map((question, index) => (
                      <div key={index} className="bg-white rounded-xl shadow p-5 border border-gray-200">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-3">
                            <span className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-indigo-600 text-white text-sm font-bold">
                              {question.questionNo ?? index + 1}
                            </span>
                            <input
                              type="number"
                              min={1}
                              value={question.questionNo ?? index + 1}
                              onChange={(e) => handleQuestionChange(index, 'questionNo', Number(e.target.value))}
                              className="w-24 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                            />
                          </div>
                        </div>

                        <div className="space-y-3">
                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Question</label>
                            <textarea
                              rows={2}
                              value={question.question || ''}
                              onChange={(e) => handleQuestionChange(index, 'question', e.target.value)}
                              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                            />
                          </div>

                          {['selA', 'selB', 'selC', 'selD'].map((optionKey) => (
                            <div key={optionKey}>
                              <label className="block text-xs font-semibold text-gray-600 mb-1">
                                {optionKey.toUpperCase()}
                              </label>
                              <input
                                type="text"
                                value={question[optionKey] || ''}
                                onChange={(e) => handleQuestionChange(index, optionKey, e.target.value)}
                                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                              />
                            </div>
                          ))}

                          <div>
                            <label className="block text-xs font-semibold text-gray-600 mb-1">Answer</label>
                            <select
                              value={question.answer || 'A'}
                              onChange={(e) => handleQuestionChange(index, 'answer', e.target.value)}
                              className="w-32 border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                            >
                              {['A', 'B', 'C', 'D'].map((option) => (
                                <option key={option} value={option}>
                                  {option}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>

                  <div className="mt-8 flex justify-end">
                    <button
                      type="button"
                      onClick={handleSaveAssessment}
                      disabled={savingAssessment}
                      className="px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg shadow hover:bg-indigo-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                    >
                      {savingAssessment ? 'Saving...' : 'Save Assessment'}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="bg-white rounded-2xl shadow-xl p-8">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">Current Assessments</h2>

              {listError && !loading && (
                <div className="mb-4 rounded-lg bg-red-50 px-4 py-3 text-sm font-semibold text-red-700">
                  {listError}
                </div>
              )}
              {loading ? (
                <div className="flex justify-center items-center py-12">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
                </div>
              ) : assessments.length === 0 ? (
                <div className="text-center py-12">
                  <p className="text-gray-500 text-lg">No assessments found</p>
                  <p className="text-gray-400 mt-2">Create or save an assessment to see it here</p>
                </div>
              ) : (
                <div className="-mx-4 overflow-x-auto sm:mx-0">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Code
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Scope / Role
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Category
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Difficulty
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Questions
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Updated
                        </th>
                        <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-600">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 bg-white">
                      {assessments.map((assessment) => {
                        const rowKey = assessment.assessmentCode || assessment._id || assessment.id;
                        const deleteKey = assessment.assessmentCode || assessment._id || rowKey;
                        const scopeLabel = assessment.scope
                          ? assessment.scope.charAt(0).toUpperCase() + assessment.scope.slice(1)
                          : 'Global';
                        const questionTotal = assessment.questionCount || (assessment.questions?.length ?? 0);
                        const questionList = Array.isArray(assessment.questions)
                          ? assessment.questions
                          : [];
                        const isExpanded = expandedRows.includes(rowKey);

                        return (
                          <Fragment key={rowKey}>
                            <tr>
                              <td className="px-4 py-3 text-sm font-semibold text-gray-900">
                                {assessment.assessmentCode || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                <div className="font-semibold text-gray-900">{scopeLabel}</div>
                                {assessment.role && (
                                  <div className="text-xs text-gray-500">Role: {assessment.role}</div>
                                )}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {assessment.category || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {assessment.difficulty || 'N/A'}
                              </td>
                              <td className="px-4 py-3 text-sm text-gray-700">{questionTotal}</td>
                              <td className="px-4 py-3 text-sm text-gray-700">
                                {formatDateTime(assessment.updatedAt || assessment.createdAt)}
                              </td>
                              <td className="px-4 py-3 text-right text-sm">
                                <div className="flex justify-end gap-2">
                                  <button
                                    type="button"
                                    onClick={() => handleToggleQuestions(rowKey)}
                                    className="inline-flex items-center rounded-lg bg-indigo-600 px-4 py-2 font-semibold text-white shadow hover:bg-indigo-700 transition"
                                  >
                                    {isExpanded ? 'Hide Questions' : 'View Questions'}
                                  </button>
                                  <button
                                    type="button"
                                    onClick={() => handleDeleteAssessment(assessment)}
                                    disabled={deletingCode === deleteKey}
                                    className="inline-flex items-center rounded-lg bg-red-600 px-4 py-2 font-semibold text-white shadow hover:bg-red-700 transition disabled:bg-gray-400 disabled:cursor-not-allowed"
                                  >
                                    {deletingCode === deleteKey ? 'Deleting...' : 'Delete'}
                                  </button>
                                </div>
                              </td>
                            </tr>
                            {isExpanded && (
                              <tr>
                                <td colSpan={7} className="bg-gray-50 px-6 py-5 text-sm text-gray-700">
                                  {questionList.length === 0 ? (
                                    <p className="text-gray-500">No questions stored for this assessment.</p>
                                  ) : (
                                    <div className="space-y-4">
                                      {questionList.map((question, index) => (
                                        <div
                                          key={`${rowKey}-question-${index}`}
                                          className="rounded-xl border border-gray-200 bg-white p-4 shadow-sm"
                                        >
                                          <div className="flex items-start justify-between">
                                            <div>
                                              <p className="text-xs font-semibold uppercase text-gray-500">
                                                Question {question.questionNo ?? index + 1}
                                              </p>
                                              <p className="mt-1 text-gray-900">{question.question || 'Untitled'}</p>
                                            </div>
                                            <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-semibold text-indigo-700">
                                              Answer: {question.answer || 'N/A'}
                                            </span>
                                          </div>
                                          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
                                            {['selA', 'selB', 'selC', 'selD'].map((optionKey) => (
                                              <div key={optionKey} className="rounded-lg bg-gray-50 px-3 py-2">
                                                <p className="text-xs font-semibold text-gray-500">
                                                  {optionKey.toUpperCase()}
                                                </p>
                                                <p className="text-gray-800">
                                                  {question[optionKey] || 'Not provided'}
                                                </p>
                                              </div>
                                            ))}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
